// Full offline download for supporters and schools (F12). Framework-free; the panel and the updater call it.
// Files go into the same caches the service worker serves from, so a downloaded atlas works with no connection.
import { cacheFor } from './caches';

export type OfflineFile = { url: string; bytes: number; sha256: string };
export type OfflineManifest = { version: string; bytes: number; files: OfflineFile[] };
/** What's been downloaded: url → sha256. Kept in the Cache API (no localStorage identifiers). */
export type OfflineState = { version: string; files: Record<string, string> };

const STATE_CACHE = 'marrow-offline';
const STATE_URL = '/__marrow/offline-state.json';
const MANIFEST_COPY = '/__marrow/offline-manifest.json';
/** Marks a request to skip the service worker's caches (see WORKBOX in vite.config.ts). */
const fresh = (url: string) => `${url}${url.includes('?') ? '&' : '?'}fresh=1`;

/** The files to fetch (missing or changed hash) and the ones no longer needed. Pure. */
export function plan(m: OfflineManifest, s: OfflineState | null): { fetch: OfflineFile[]; bytes: number; stale: string[] } {
  const have = s?.files ?? {};
  const fetch = m.files.filter((f) => have[f.url] !== f.sha256);
  const live = new Set(m.files.map((f) => f.url));
  return { fetch, bytes: fetch.reduce((a, f) => a + f.bytes, 0), stale: Object.keys(have).filter((u) => !live.has(u)) };
}

export async function readState(): Promise<OfflineState | null> {
  if (typeof caches === 'undefined') return null;
  const r = await (await caches.open(STATE_CACHE)).match(STATE_URL);
  return r ? ((await r.json()) as OfflineState) : null;
}

async function writeState(s: OfflineState): Promise<void> {
  await (await caches.open(STATE_CACHE)).put(STATE_URL, new Response(JSON.stringify(s), { headers: { 'content-type': 'application/json' } }));
}

/** The current offline manifest from the network; offline, the copy saved with the last download. */
export async function fetchManifest(): Promise<OfflineManifest> {
  try {
    const r = await fetch(fresh('/offline-manifest.json'), { cache: 'no-cache' });
    if (!r.ok) throw new Error(`offline manifest: HTTP ${r.status}`);
    return (await r.json()) as OfflineManifest;
  } catch (e) {
    const copy = typeof caches !== 'undefined' ? await (await caches.open(STATE_CACHE)).match(MANIFEST_COPY) : undefined;
    if (copy) return (await copy.json()) as OfflineManifest;
    throw e;
  }
}

async function sha256(buf: ArrayBuffer): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function isQuotaError(e: unknown): boolean {
  return e instanceof DOMException ? e.name === 'QuotaExceededError' || e.code === 22 : /quota/i.test(String((e as Error)?.message ?? e));
}

export const mb = (bytes: number) => `${Math.max(0.1, Math.round(bytes / 1e5) / 10)} MB`;

export type DownloadResult =
  | { ok: true; fetched: number; bytes: number }
  | { ok: false; reason: 'quota' | 'network' | 'integrity'; message: string; fetched: number };

/**
 * Downloads every missing or changed file, verifying each hash, then records what's stored.
 * Progress is reported in bytes. Stops cleanly on errors, keeping what was already saved.
 */
export async function download(opts: { manifest?: OfflineManifest; onProgress?: (done: number, total: number) => void; concurrency?: number } = {}): Promise<DownloadResult> {
  const manifest = opts.manifest ?? (await fetchManifest());
  const state: OfflineState = (await readState()) ?? { version: '', files: {} };
  const todo = plan(manifest, state);

  // Fail fast when the browser says the space isn't there.
  const est = await navigator.storage?.estimate?.().catch(() => undefined);
  if (est?.quota !== undefined && est.usage !== undefined && est.quota - est.usage < todo.bytes) {
    return { ok: false, reason: 'quota', fetched: 0, message: quotaMessage(todo.bytes, est.quota - est.usage) };
  }
  void navigator.storage?.persist?.().catch(() => false);

  let done = 0;
  let fetched = 0;
  let failure: DownloadResult | null = null;
  const queue = [...todo.fetch];
  const worker = async () => {
    while (queue.length && !failure) {
      const f = queue.shift()!;
      try {
        const res = await fetch(fresh(f.url), { cache: 'no-cache' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await res.arrayBuffer();
        if ((await sha256(buf)) !== f.sha256) {
          failure = { ok: false, reason: 'integrity', fetched, message: 'A file didn’t download correctly. Please try again.' };
          return;
        }
        const headers = new Headers(res.headers);
        await (await caches.open(cacheFor(f.url))).put(f.url, new Response(buf, { status: 200, headers }));
        state.files[f.url] = f.sha256;
        fetched++;
        done += f.bytes;
        opts.onProgress?.(done, todo.bytes);
      } catch (e) {
        failure = isQuotaError(e)
          ? { ok: false, reason: 'quota', fetched, message: quotaMessage(todo.bytes - done) }
          : { ok: false, reason: 'network', fetched, message: 'The download stopped. Check your connection and try again; it will pick up where it left off.' };
        return;
      }
    }
  };
  await Promise.all(Array.from({ length: opts.concurrency ?? 4 }, worker));

  for (const url of todo.stale) {
    await (await caches.open(cacheFor(url))).delete(url);
    delete state.files[url];
  }
  if (!failure) {
    state.version = manifest.version;
    await (await caches.open(STATE_CACHE)).put(MANIFEST_COPY, new Response(JSON.stringify(manifest))).catch(() => undefined);
  }
  // Record progress even on failure, so a retry resumes rather than restarts (best effort if storage is full).
  await writeState(state).catch(() => undefined);
  return failure ?? { ok: true, fetched, bytes: todo.bytes };
}

export function quotaMessage(needed: number, available?: number): string {
  const room = available !== undefined ? ` Your browser is allowing ${mb(Math.max(0, available))}.` : '';
  return `Not enough storage space: Marrow needs about ${mb(needed)} more.${room} Free up some space on this device and try again. Everything you’ve viewed still works offline.`;
}

/** Removes the full download (files and record). Ordinary browsing caches refill as you go. */
export async function remove(): Promise<void> {
  const s = await readState();
  if (!s) return;
  for (const url of Object.keys(s.files)) await (await caches.open(cacheFor(url))).delete(url);
  await caches.delete(STATE_CACHE);
}

export async function usage(): Promise<{ usage: number; quota: number } | null> {
  const e = await navigator.storage?.estimate?.().catch(() => undefined);
  return e?.usage !== undefined && e.quota !== undefined ? { usage: e.usage, quota: e.quota } : null;
}

/**
 * Called on start-up when online. If a full download exists and the content has changed, fetches only the changed
 * files in the background. Resolves true when something new was stored (the app then offers a reload).
 */
export async function refreshIfDownloaded(): Promise<boolean> {
  const s = await readState();
  if (!s || !s.version) return false;
  const m = await fetchManifest();
  if (m.version === s.version) return false;
  const r = await download({ manifest: m, concurrency: 2 });
  return r.ok && r.fetched > 0;
}
